import "./App.scss";
import WOW from "wowjs";
import AboutUs from "./components/About";
import Services from "./components/Services";
import Portfolio from "./components/Portfolio";
import Contact from "./components/Contact";
import Footer from "./components/Footer";
import { useEffect, useState } from "react";
import {
  appliedConfig,
  introBackgroundConfig,
  themeConfig,
} from "./config/commonConfig";
import { Tooltip } from "./components/Tooltip";
import CallToAction from "./components/CallToAction";
import FAQs from "./components/FAQs";
import FullScreenVideoBgIntro from "./components/themes/fullScreen/IntroVideoBg";
import FullScreenHeader from "./components/themes/fullScreen/Header";
import FullScreenDefaultIntro from "./components/themes/fullScreen/IntroDefault";
import FullScreenImgBgIntro from "./components/themes/fullScreen/IntroImageBg";
import BottomHeader from "./components/themes/bottomHeader/Header";
import StandardMenuHeader from "./components/themes/StandardMenu/Header";
import BottomHeaderDefaultIntro from "./components/themes/bottomHeader/IntroDefault";
import BottomHeaderImgBgIntro from "./components/themes/bottomHeader/IntroImageBg";
import BottomHeaderVideoBgIntro from "./components/themes/bottomHeader/IntroVideoBg";
import StandardMenuDefaultIntro from "./components/themes/StandardMenu/IntroDefault";
import StandardMenuImgBgIntro from "./components/themes/StandardMenu/IntroImageBg";
import StandardMenuVideoBgIntro from "./components/themes/StandardMenu/IntroVideoBg";
import { TranslateProvider } from "./context/TranslateContext";
import { ThemeProvider } from "./context/ThemeContext";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import Resume from "./components/Resume";

// Translation API proxy URL (backend server that securely holds the Google API key)
const TRANSLATE_API_URL =
  process.env.REACT_APP_TRANSLATE_API_URL || "https://api.ramirez-carlos.com";

// reCAPTCHA v3 Site Key (public - safe to expose)
const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || "YOUR_RECAPTCHA_SITE_KEY";

// ─── Main portfolio site component ───────────────────────────────────────────
function MainSite() {
  const appliedTheme = appliedConfig.appliedTheme;
  const appliedIntro = appliedConfig.appliedIntro;

  const handleNavClick = (section) => {
    document.getElementById(section).scrollIntoView({ behavior: "smooth" });
  };

  const [scrollTopVisible, setScrollTopVisible] = useState(false);

  useEffect(() => {
    new WOW.WOW({ live: false }).init();
  }, []);

  const checkScrollTop = () => {
    const scrollTopBtn = document.getElementById("back-to-top");
    if (scrollTopBtn) {
      const scrolled =
        document.body.scrollTop > 400 ||
        document.documentElement.scrollTop > 400;
      setScrollTopVisible(scrolled);
    }
  };

  if (typeof window !== "undefined") {
    window.addEventListener("scroll", checkScrollTop);
  }

  const getHeader = () => {
    if (appliedTheme === themeConfig.BottomHeader) {
      return <BottomHeader />;
    } else if (appliedTheme === themeConfig.FullScreenMenu) {
      return (
        <FullScreenHeader
          textWhite={appliedIntro === introBackgroundConfig.image}
        />
      );
    } else {
      return <StandardMenuHeader />;
    }
  };

  const getBottomHeaderIntro = () => {
    if (appliedIntro === introBackgroundConfig.default)
      return <BottomHeaderDefaultIntro />;
    if (appliedIntro === introBackgroundConfig.image)
      return <BottomHeaderImgBgIntro />;
    return <BottomHeaderVideoBgIntro />;
  };

  const getFullScreenIntro = () => {
    if (appliedIntro === introBackgroundConfig.default)
      return <FullScreenDefaultIntro />;
    if (appliedIntro === introBackgroundConfig.image)
      return <FullScreenImgBgIntro />;
    return <FullScreenVideoBgIntro />;
  };

  const getStandardMenuIntro = () => {
    if (appliedIntro === introBackgroundConfig.default)
      return <StandardMenuDefaultIntro />;
    if (appliedIntro === introBackgroundConfig.image)
      return <StandardMenuImgBgIntro />;
    return <StandardMenuVideoBgIntro />;
  };

  return (
    <>
      {/* ── Skip to content — screen readers & keyboard users ── */}
      <a href="#content" className="skip-to-content">
        Skip to main content
      </a>

      <div style={{ position: "relative" }}>
        <div id="main-wrapper">
          {appliedTheme === themeConfig.BottomHeader && getBottomHeaderIntro()}
          {getHeader()}

          {/* tabIndex={-1} lets the skip link focus land here */}
          <div id="content" role="main" tabIndex={-1}>
            {appliedTheme === themeConfig.FullScreenMenu && getFullScreenIntro()}
            {appliedTheme === themeConfig.StandardMenu && getStandardMenuIntro()}

            <AboutUs />
            <Services />
            {/* <Resume /> */}
            <Portfolio />
            <CallToAction />
            <FAQs />
            {/* <Testimonials /> */}
            <Contact />
          </div>

          <Footer handleNavClick={handleNavClick} />
        </div>

        {/* Back to top */}
        <Tooltip text="Back to Top" placement="left">
          <span
            id="back-to-top"
            className="rounded-circle"
            role="button"
            aria-label="Back to top"
            tabIndex={0}
            style={{ display: scrollTopVisible ? "inline" : "none" }}
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ")
                window.scrollTo({ top: 0, behavior: "smooth" });
            }}
          >
            <i className="fas fa-arrow-up" aria-hidden="true" />
          </span>
        </Tooltip>
      </div>
    </>
  );
}

// ─── Root App — provider stack ────────────────────────────────────────────────
function App() {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHA_SITE_KEY}
      scriptProps={{ async: true, defer: true, appendTo: "head" }}
    >
      <TranslateProvider apiUrl={TRANSLATE_API_URL}>
        <ThemeProvider>
          <Router>
            <Routes>
              <Route path="/r/resume" element={<Resume />} />
              <Route path="/*" element={<MainSite />} />
            </Routes>
          </Router>
        </ThemeProvider>
      </TranslateProvider>
    </GoogleReCaptchaProvider>
  );
}

export default App;
